import {
  BasketballGameCarInformation,
  Car,
  CarReservation,
  ResponseBaseGetCars,
  ResponseBaseGetCarsReservations,
  SelectOption,
} from '@hefe/models';
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import API from '../../../api/api';
import { isCacheValid } from '../../../utils/cache/caching';

const FEATURE_KEY = 'cars';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */
export const fetchCars = createAsyncThunk(
  'cars/getAll',
  async (
    data: {
      cacheDuration: number;
    },
    thunkAPI
  ) => {
    if (!isCacheValid('cars/getAll', data, thunkAPI)) {
      const response = await API.cars().getAll();
      return response?.data;
    }
  }
);

export const createCar = createAsyncThunk(
  'cars/create',
  async (data: Car, thunkAPI) => {
    const response = await API.cars().create(data);
    return response?.data;
  }
);

export const updateCar = createAsyncThunk(
  'cars/update',
  async (data: Car, thunkAPI) => {
    const response = await API.cars().update(data);
    return response?.data;
  }
);

export const deleteCar = createAsyncThunk<any, Car>(
  'cars/delete',
  async (car: Car, thunkAPI) => {
    const response = await API.cars().delete(car);
    if (response) {
      return thunkAPI.fulfillWithValue(car);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

/***************************************************
 *                RESERVATIONS
 ***************************************************/
export const fetchCarsReservations = createAsyncThunk(
  'cars/reservations/getAll',
  async (_, thunkAPI) => {
    const response = await API.carReservations().getAll();
    return response?.data;
  }
);

export const createCarReservation = createAsyncThunk(
  'cars/reservations/create',
  async (data: CarReservation, thunkAPI) => {
    const response = await API.carReservations().create(data);
    return response?.data;
  }
);

export const updateCarReservation = createAsyncThunk(
  'cars/reservations/update',
  async (data: CarReservation, thunkAPI) => {
    const response = await API.carReservations().update(data);
    return response?.data;
  }
);

export const deleteCarReservation = createAsyncThunk<any, CarReservation>(
  'cars/reservations/delete',
  async (reservation: CarReservation, thunkAPI) => {
    const response = await API.carReservations().delete(reservation);
    if (response) {
      return thunkAPI.fulfillWithValue(reservation);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);
/***************************************************
 *                BASKETBALL GAME INFOS
 ***************************************************/
export const fetchBasketballGameCarsInformation = createAsyncThunk<any, any>(
  'cars/basketballGame/getCarsInformation',
  async (
    data: {
      cacheDuration: number;
      gameId: number;
      teamId: number;
    },
    thunkAPI
  ) => {
    if (
      !isCacheValid('cars/basketballGame/getCarsInformation', data, thunkAPI)
    ) {
      const response = await API.basketballGames().getCarsInformation(
        data.gameId,
        data.teamId
      );
      if (response) {
        return thunkAPI.fulfillWithValue({
          gameId: data.gameId,
          teamId: data.teamId,
          data: response.data,
        });
      }
    }

    return thunkAPI.fulfillWithValue(undefined);
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface CarsState {
  cars: Car[];
  reservations: CarReservation[];
  basketballGameInformation: {
    [key1: number]: {
      [key2: number]: BasketballGameCarInformation[];
    };
  };
}

const initialState: CarsState = {
  cars: [],
  reservations: [],
  basketballGameInformation: {},
};

export const CarsSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetchCars.fulfilled,
      (state: CarsState, action: PayloadAction<ResponseBaseGetCars>) => {
        if (action.payload && action.payload.data) {
          state.cars = action.payload.data;
        }
      }
    );
    builder.addCase(
      createCar.fulfilled,
      (state: CarsState, action: PayloadAction<Car>) => {
        state.cars.push(action.payload);
      }
    );
    builder.addCase(
      updateCar.fulfilled,
      (state: CarsState, action: PayloadAction<Car>) => {
        state.cars = state.cars.map((car) =>
          car.id === action.payload.id ? action.payload : car
        );
      }
    );
    builder.addCase(
      deleteCar.fulfilled,
      (state: CarsState, action: PayloadAction<Car>) => {
        state.cars = state.cars.filter((car) => car.id !== action.payload.id);
      }
    );
    /***************************************************
     *                RESERVATIONS
     ***************************************************/
    builder.addCase(
      fetchCarsReservations.fulfilled,
      (
        state: CarsState,
        action: PayloadAction<ResponseBaseGetCarsReservations>
      ) => {
        if (action.payload) {
          state.reservations = action.payload;
        }
      }
    );
    builder.addCase(
      createCarReservation.fulfilled,
      (state: CarsState, action: PayloadAction<CarReservation>) => {
        state.reservations.push(action.payload);
        // TODO Order by date!
      }
    );
    builder.addCase(
      updateCarReservation.fulfilled,
      (state: CarsState, action: PayloadAction<CarReservation>) => {
        state.reservations = state.reservations.map((reservation) =>
          reservation.id === action.payload.id ? action.payload : reservation
        );
      }
    );
    builder.addCase(
      deleteCarReservation.fulfilled,
      (state: CarsState, action: PayloadAction<CarReservation>) => {
        state.reservations = state.reservations.filter(
          (reservation) => reservation.id !== action.payload.id
        );
      }
    );
    /***************************************************
     *                BASKETBALL GAME INFOS
     ***************************************************/
    builder.addCase(
      fetchBasketballGameCarsInformation.fulfilled,
      (
        state: CarsState,
        action: PayloadAction<{
          gameId: number;
          teamId: number;
          data: BasketballGameCarInformation[];
        }>
      ) => {
        if (action?.payload?.data) {
          const { gameId, teamId, data } = action.payload;
          if (!state.basketballGameInformation[gameId]) {
            state.basketballGameInformation[gameId] = {};
          }

          state.basketballGameInformation[gameId][teamId] = data;
        }
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const carsReducer = CarsSlice.reducer;
export const carsActions = CarsSlice.actions;

const state = (rootState: any): CarsState => rootState[FEATURE_KEY];

export const carsSelector = createSelector(state, (state) => {
  return state.cars;
});

export const carsSelectSelector = createSelector(state, (state) => {
  return state.cars.map((car) => {
    const option: SelectOption = {
      label: `${car.manufacturer} ${car.model}`,
      value: car.id,
    };
    return option;
  });
});

export const reservableCarsSelectSelector = createSelector(state, (state) => {
  return state.cars
    .filter((car) => car.reservable)
    .map((car) => {
      const option: SelectOption = {
        label: `${car.manufacturer} ${car.model}`,
        value: car.id,
      };
      return option;
    });
});

export const carsReservationsSelector = createSelector(state, (state) => {
  return state.reservations;
});

export const basketballGameCarsInformationSelector = createSelector(
  state,
  (state) => {
    return state.basketballGameInformation;
  }
);
